import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from '@/components/feature/sections/HeroSection';
import TrustBar from '@/components/feature/sections/TrustBar';
import ServicesOverview from '@/components/feature/sections/ServicesOverview';
import AIAnswerBlock from '@/components/feature/sections/AIAnswerBlock';
import ProcessSection from '@/components/feature/sections/ProcessSection';
import WhyChooseUs from '@/components/feature/sections/WhyChooseUs';
import ServiceAreas from '@/components/feature/sections/ServiceAreas';
import Testimonials from '@/components/feature/sections/Testimonials';
import FAQSection from '@/components/feature/sections/FAQSection';
import FinalCTA from '@/components/feature/sections/FinalCTA';
import {
  HERO_IMAGES, CHEF_PHOTO, FOOD_IMAGES, SECTION_IMAGES,
  SHARED_TRUST_STATS, SHARED_SERVICE_AREAS, SHARED_TESTIMONIALS,
} from '@/mocks/brandData';

export default function Services() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <HeroSection
          headline="Full-Service Catering & Personal Chef Excellence in San Antonio"
          subheadline="From intimate private dinners to grand celebrations — every service is delivered with 20+ years of culinary expertise, prepared entirely from scratch."
          ctaText="Get a Custom Quote"
          ctaHref="#quote"
          secondaryCta={{ text: 'Call 210-858-7293', href: 'tel:2108587293' }}
          backgroundImage={HERO_IMAGES[1]}
          badge="American Personal Chef Association"
        />
        <TrustBar stats={SHARED_TRUST_STATS} />
        <ServicesOverview
          eyebrow="Our Services"
          headline="Every Service, Crafted to Perfection"
          description="Whether you need a personal chef for weekly meals or full catering for a 100-person gala, JC Cooks delivers an uncompromising culinary experience."
          services={[
            { image: FOOD_IMAGES[0], tag: 'Personal', title: 'Private Chef Service', description: 'In-home chef experience with fully custom menus, fresh ingredients, and complete kitchen cleanup.', href: '#quote' },
            { image: FOOD_IMAGES[1], tag: 'Celebrations', title: 'Weddings & Galas', description: 'Elegant full-service catering for your most memorable occasions — food, staff, service, and cleanup.', href: '#quote' },
            { image: FOOD_IMAGES[2], tag: 'Corporate', title: 'Corporate & Office Events', description: 'Professional catering for business lunches, corporate dinners, and company celebrations of any size.', href: '#quote' },
          ]}
        />
        <AIAnswerBlock
          eyebrow="Service Details"
          headline="What JC Cooks Includes With Every Service"
          overview="Every JC Cooks engagement begins with a personal consultation to understand your event needs, dietary preferences, and flavor profile. Chef Joyce then designs a completely custom menu, sources the freshest ingredients, and prepares every dish entirely from scratch — delivering a restaurant-quality experience in your chosen venue."
          services={[
            'Custom menu consultation and personal design',
            'Fresh, locally-sourced ingredient procurement',
            'Complete from-scratch food preparation on-site',
            'Professional event staffing and table service',
            'Full setup and post-event cleanup',
            'Dietary accommodations: gluten-free, vegan, allergy-safe',
            'Holiday, corporate, and weekly meal programs',
          ]}
          benefits={[
            'No pre-packaged or frozen food — ever, under any circumstances',
            'Every menu tailored to your exact preferences and dietary needs',
            'Serves parties from 10 to 100+ guests with equal commitment',
            'Trained under the late culinary expert Sharon Shipley',
            'Over 20 years serving San Antonio private and corporate clients',
            'Award-winning — Thumbtack Best of 2016 & 2017',
          ]}
        />
        <ProcessSection
          headline="How the JC Cooks Service Works"
          subheadline="A simple, stress-free process from first call to final bite."
          steps={[
            { number: '1', title: 'Book a Consultation', description: 'Call or request a quote. We discuss your event vision, guest count, date, and any special needs.', icon: 'ri-calendar-check-line' },
            { number: '2', title: 'Menu Design', description: 'Chef Joyce crafts a custom menu around your taste preferences and dietary requirements.', icon: 'ri-quill-pen-line' },
            { number: '3', title: 'Day-of Execution', description: 'We arrive, set up, cook everything fresh on-site, and serve your guests with professional care.', icon: 'ri-restaurant-2-line' },
            { number: '4', title: 'Full Cleanup', description: 'After the last course, we handle all cleanup — leaving your space exactly as we found it.', icon: 'ri-sparkling-line' },
          ]}
        />
        <WhyChooseUs
          image={CHEF_PHOTO}
          eyebrow="The JC Cooks Difference"
          headline="Not Just Catering — A Complete Culinary Experience"
          benefits={[
            { icon: 'ri-scissors-cut-line', title: 'Scratch Cooking Only', description: 'Every sauce, every side, every entrée made from scratch. We never serve reheated or pre-packaged food.' },
            { icon: 'ri-user-star-line', title: '20+ Years of Mastery', description: 'Chef Joyce Cunha\'s decades of experience means your event is in exceptionally capable hands.' },
            { icon: 'ri-calendar-event-line', title: 'Any Occasion, Any Scale', description: 'Small office lunches, elegant weddings, holiday parties — JC Cooks handles them all with equal dedication.' },
            { icon: 'ri-shield-star-line', title: 'Fully Professional Service', description: 'Our team operates with full professionalism, from attire to service standards, at every single event.' },
          ]}
          cta={{ text: 'Request Your Quote', href: '#quote' }}
        />
        <ServiceAreas
          headline="Serving San Antonio & Surrounding Communities"
          description="JC Cooks brings premium catering and personal chef services to homes and venues throughout the greater San Antonio area."
          primary="San Antonio"
          areas={SHARED_SERVICE_AREAS}
          showMap={false}
        />
        <Testimonials
          headline="Clients Who Trust JC Cooks"
          testimonials={SHARED_TESTIMONIALS}
          cta={{ text: 'Book Your Service', href: '#quote' }}
        />
        <FAQSection
          headline="Services — Common Questions"
          faqs={[
            { question: 'What catering services does JC Cooks offer in San Antonio?', answer: 'JC Cooks offers private chef service, event catering, wedding catering, corporate catering, holiday meal planning, and weekly meal preparation. All services include custom menu design, scratch-cooked food, staffing, and full cleanup. Call 210-858-7293 for details.' },
            { question: 'How much does a personal chef cost in San Antonio?', answer: 'JC Cooks pricing is custom-quoted based on menu complexity, guest count, and services required. Call 210-858-7293 or submit a quote request online. You select your menu and provide your guest count to receive accurate, personalized pricing.' },
            { question: 'Can JC Cooks cater a wedding in San Antonio?', answer: 'Yes — JC Cooks provides full wedding catering services in San Antonio including custom menus, professional service staff, setup, and complete cleanup. Chef Joyce Cunha personally oversees every wedding to ensure an exceptional dining experience for your guests.' },
            { question: 'Does JC Cooks offer weekly meal prep services?', answer: 'Yes — JC Cooks offers weekly meal preparation for individuals and families looking to eat healthy, home-cooked meals without the daily effort. Chef Joyce prepares fresh, customized meals based on your dietary preferences and stores them for the week ahead.' },
            { question: 'How far in advance should I book JC Cooks?', answer: 'For large events like weddings or galas, booking 2–4 weeks in advance is recommended. For smaller events or weekly meal service, shorter lead times are often available. Contact us at 210-858-7293 to check current availability.' },
            { question: 'Does JC Cooks cater corporate events in San Antonio?', answer: 'Yes — JC Cooks provides professional corporate catering for San Antonio businesses including office lunches, board dinners, team celebrations, and client events. We handle everything from menu design to setup, service, and cleanup for any size company event.' },
          ]}
        />
        <FinalCTA
          headline="Let's Plan Your Perfect Event Together"
          subtext="Request a custom quote today. Chef Joyce will contact you to discuss your menu, guest count, and vision."
          backgroundImage={SECTION_IMAGES.cta2}
        />
      </main>
      <Footer />
    </div>
  );
}
